import { useEffect, useMemo, useState } from "react";

import useService from "../../../../hooks/useService";
import { getModalHandler } from "../../Common/Modals/hooks/useModalSingleton";
import {
  buildSettlementPaymentAddPayload,
  buildSettlementPaymentDirtyPayload,
  isSettlementPaymentFormDirty,
  isSettlementPaymentSaveDisabled,
  mapSettlementPaymentToFormValues,
} from "../components/payments/helpers/functions";
import { useSettlementStore } from "../store/settlementStore";

export default function useSettlementPaymentsModal({
  caseId,
  mode = "edit",
  openModal,
  paymentData,
  handleCloseModal,
  onSaveSuccess,
}) {
  const settlementCheckRepository = useService("repositories/settlement-check-repository");
  const { openFailureModal, openSuccessModal } = getModalHandler();
  const bumpRefreshVersion = useSettlementStore((state) => state.bumpRefreshVersion);
  const initialFormValues = useMemo(() => mapSettlementPaymentToFormValues(paymentData), [paymentData]);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (openModal) {
      setFormValues(initialFormValues);
      setIsSaving(false);
    }
  }, [initialFormValues, openModal]);

  const isDirty = useMemo(
    () => isSettlementPaymentFormDirty(initialFormValues, formValues),
    [formValues, initialFormValues]
  );
  const isSaveDisabled = useMemo(() => isSettlementPaymentSaveDisabled(formValues), [formValues]);

  const handleFieldOnChange = (fieldName, value) => {
    setFormValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleCancel = () => {
    setFormValues(initialFormValues);
  };

  const handleSave = async () => {
    if (!isDirty || isSaveDisabled) {
      return;
    }

    try {
      setIsSaving(true);

      if (mode === "add") {
        await settlementCheckRepository.post(buildSettlementPaymentAddPayload(formValues, caseId));
      } else if (paymentData?.id) {
        await settlementCheckRepository.patch(
          paymentData.id,
          buildSettlementPaymentDirtyPayload(initialFormValues, formValues)
        );
      } else {
        return;
      }

      await onSaveSuccess?.();
      bumpRefreshVersion();
      openSuccessModal();
      handleCloseModal();
    } catch (error) {
      openFailureModal(error);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    formValues,
    isDirty,
    isSaving,
    isSaveDisabled,
    handleFieldOnChange,
    handleCancel,
    handleSave,
  };
}
